import { html, TemplateResult } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { WEB_COMPONENT_GZ_RM_TOP_IDENTITIES_RISK_WIDGET } from '../../../../webcomponents.constants';
import { RmScoreBreakdown, RmTopIdentitiesAtRiskPayload } from '@nirvana/shared/dtos/types/widgets/common';
import { ContextMenuOverlayElement } from '../../../../ui/gzContextMenuOverlay';
import { DEFAULT_RM_WIDGET_TOP_ROWS } from '@nirvana/shared/constants';
import { Row, RowConfig } from '../../../../ui/gzBasicTable/types/types';
import { renderNumber, renderText } from '../../text';
import { FontSizes } from '../../../../types/common/types';
import { fillArray } from '@nirvana/shared/basic-types/array';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { ColumnKeys as UsersAppColumnKeys } from '../../../usersApp/types';
import { LinkType } from '../../../../ui/gzLinkComponent/types';
import { translateFn } from '../../../../utils/translator.helper';
import { buildOverlayContent, closeOverlay } from '../../../common/customRenderers';
import { RiskTypes, State } from '../../../common/constants';
import { computeMaxRowsFit, fitItemsToWidget } from '../../resize';
import { renderIdentityMonitoringDisabled } from '../../identity.monitoring';

/**
 * @summary Type here the description of this component
 * @tag gz-rm-top-identities-risk-widget
 */
@customElement(WEB_COMPONENT_GZ_RM_TOP_IDENTITIES_RISK_WIDGET)
export class GzRmTopIdentitiesRiskWidgetWebComponent extends GzGenericWidgetContent {
  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd;

  @property({ type: Object, attribute: true })
  payload: RmTopIdentitiesAtRiskPayload = { topIdentitiesAtRisk: { companyId: '', total: 0, items: [], identityMonitoringDisabled: false } };

  /**
   * WARNING: this query MUST be cached, because after the first render of the overlay, it will
   * be removed from the shadow DOM and replaced by a placeholder while open.
   */
  @query('#risk-overlay', true)
  overlay!: ContextMenuOverlayElement;

  static get styles() {
    return styles;
  }

  protected viewAll(): void {
    NavigationHelper.navigateToRiskManagement({
      company: { id: this.payload.topIdentitiesAtRisk?.companyId ?? '' },
      filters: [
        {
          [UsersAppColumnKeys.STATE]: { in: [State.ACTIVE] },
        },
        {
          [UsersAppColumnKeys.RISK_SCORE]: { range: { min: 1, max: 100 } },
        },
      ],
      section: 'identities',
      newTab: true,
    });
  }

  protected renderLoader(): TemplateResult {
    return html` <gz-loader class="pt-1" .rows="${computeMaxRowsFit(this.contentHeight)}" columns="1"></gz-loader>`;
  }

  protected renderContent(): TemplateResult {
    if (this.payload.topIdentitiesAtRisk?.identityMonitoringDisabled) {
      return renderIdentityMonitoringDisabled();
    }

    const items: Row[] =
      this.payload.topIdentitiesAtRisk?.items?.map(
        ({ username, type, fullName, scoreBreakdown }): RowConfig => ({
          data: { username, fullName },
          cells: [
            html` <gz-rm-icon iconSet="entity" iconClassMapType="users" type="${type}"></gz-rm-icon>`,
            renderText(fullName),
            html` <div
              class="d-contents"
              @mouseenter="${e => this.showOverlay(e, scoreBreakdown)}"
              @mouseleave="${e => this.closeOverlay()}"
            >
              <gz-rm-severity-bar score="${scoreBreakdown.finalScore}"></gz-rm-severity-bar>
            </div>`,
            renderNumber(`${scoreBreakdown.finalScore}%`),
          ],
        })
      ) ?? [];

    const grayBullet = html` <gz-bullet
      boxSize="${FontSizes.BODY}"
      size="${FontSizes.HEADING_5}"
      center
      color="var(--grey-layout-202-disabled-icons)"
    ></gz-bullet>`;
    const emptyBar = html` <gz-rm-severity-bar score="0"></gz-rm-severity-bar>`;
    const emptyItem = [grayBullet, renderText('...', true), emptyBar, renderNumber('--', true)];
    const fullItems = fitItemsToWidget(fillArray(items, DEFAULT_RM_WIDGET_TOP_ROWS, emptyItem), this.contentHeight);

    return html`
      <gz-basic-table
        style="margin-top: calc(var(--spacer)*0.3)"
        rowsClickable
        alignLastColumnRight
        .onClick="${(rowData: { username: string; fullName: string }) => {
          if (!rowData) {
            return;
          }

          const { username, fullName } = rowData;

          NavigationHelper.navigateToRiskManagement({
            company: { id: this.payload.topIdentitiesAtRisk?.companyId ?? '' },
            section: 'identities',
            filters: [
              {
                [UsersAppColumnKeys.USERNAME]: {
                  complexEnumFilter: [
                    {
                      label: fullName,
                      value: username,
                    },
                  ],
                },
              },
            ],
            newTab: true,
          });
        }}"
        .data="${fullItems}"
      ></gz-basic-table>

      <gz-context-menu-overlay id="risk-overlay" class="fit-content"></gz-context-menu-overlay>

      <gz-link type="${LinkType.QUIET}" fontSize="${FontSizes.PARAGRAPH_11}" .callback=${() => this.viewAll()}>
        ${translateFn('View all')} (${this.payload.topIdentitiesAtRisk?.total ?? 0})
      </gz-link>
    `;
  }

  private async showOverlay(event: unknown, scoreBreakdown: RmScoreBreakdown): Promise<void> {
    await buildOverlayContent(event, this.overlay, { scoreBreakdown }, RiskTypes.DEVICES);
  }

  private async closeOverlay(): Promise<void> {
    await closeOverlay(this.overlay);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-rm-top-identities-risk-widget': GzRmTopIdentitiesRiskWidgetWebComponent;
  }
}
